import React from 'react';
import { Box, Step, StepLabel, Stepper, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Check, CloudUpload, Tune, Face, ImageSearch, HighQuality, AutoAwesome } from '@mui/icons-material';

interface ProgressIndicatorProps {
  currentStep: number;
  mode: 'on-model' | 'flat-lay';
}

interface StepConfig {
  label: string;
  icon: React.ReactNode;
  description: string;
}

const onModelSteps: StepConfig[] = [
  {
    label: 'Zdjęcie źródłowe',
    icon: <CloudUpload />,
    description: 'Prześlij zdjęcie z odzieżą'
  },
  {
    label: 'Typ przeniesienia',
    icon: <Tune />,
    description: 'Wybierz element do przeniesienia'
  },
  {
    label: 'Model docelowy',
    icon: <Face />,
    description: 'Wybierz model lub prześlij własny'
  },
  {
    label: 'Studio',
    icon: <ImageSearch />,
    description: 'Wybierz tło i oświetlenie'
  },
  {
    label: 'Jakość',
    icon: <HighQuality />,
    description: 'Ustaw parametry generowania'
  },
  {
    label: 'Generowanie',
    icon: <AutoAwesome />,
    description: 'Tworzenie końcowego obrazu'
  }
];

const flatLaySteps: StepConfig[] = [
  {
    label: 'Model',
    icon: <Face />,
    description: 'Wybierz model z galerii'
  },
  {
    label: 'Część ciała',
    icon: <Tune />,
    description: 'Określ obszar odzieży'
  },
  {
    label: 'Odzież',
    icon: <CloudUpload />,
    description: 'Prześlij zdjęcie odzieży'
  },
  {
    label: 'Studio',
    icon: <ImageSearch />,
    description: 'Wybierz tło i oświetlenie'
  },
  {
    label: 'Generowanie',
    icon: <AutoAwesome />,
    description: 'Tworzenie końcowego obrazu'
  }
];

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ currentStep, mode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const steps = mode === 'on-model' ? onModelSteps : flatLaySteps;
  
  return (
    <Box sx={{ 
      width: '100%', 
      mb: 4,
      p: 3,
      backgroundColor: 'rgba(25, 118, 210, 0.02)',
      borderRadius: 3,
      border: '1px solid rgba(25, 118, 210, 0.1)'
    }}>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          color: 'primary.main',
          fontWeight: 600,
          textAlign: 'center'
        }}
      >
        Postęp procesu ({currentStep + 1}/{steps.length})
      </Typography>
      
      <Stepper 
        activeStep={currentStep} 
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{
          '& .MuiStepLabel-root': {
            cursor: 'default'
          },
          '& .MuiStepIcon-root': {
            fontSize: isMobile ? '1.5rem' : '1.8rem'
          },
          '& .MuiStepLabel-label': {
            fontSize: isMobile ? '0.875rem' : '1rem',
            fontWeight: 500
          }
        }}
      >
        {steps.map((step, index) => (
          <Step key={index} completed={index < currentStep}>
            <StepLabel
              StepIconComponent={({ active, completed }) => (
                <Box
                  sx={{
                    width: isMobile ? 36 : 40,
                    height: isMobile ? 36 : 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: completed 
                      ? theme.palette.success.main 
                      : active 
                        ? theme.palette.primary.main 
                        : theme.palette.grey[300],
                    color: completed || active ? 'white' : theme.palette.grey[600],
                    transition: 'all 0.3s ease',
                    fontSize: isMobile ? '1.2rem' : '1.4rem'
                  }}
                >
                  {completed ? <Check /> : step.icon}
                </Box>
              )}
            >
              <Box sx={{ textAlign: isMobile ? 'left' : 'center' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: index === currentStep ? 600 : 500,
                    color: index <= currentStep ? 'text.primary' : 'text.secondary'
                  }}
                >
                  {step.label}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    display: 'block',
                    mt: 0.5
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {/* Progress Bar */}
      <Box sx={{ mt: 3, mb: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            Ukończono {currentStep} z {steps.length} kroków
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight={600}>
            {Math.round((currentStep / steps.length) * 100)}%
          </Typography>
        </Box>
        <Box sx={{ 
          width: '100%', 
          height: 8, 
          backgroundColor: theme.palette.grey[200],
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            width: `${(currentStep / steps.length) * 100}%`,
            height: '100%',
            backgroundColor: theme.palette.primary.main,
            transition: 'width 0.3s ease'
          }} />
        </Box>
      </Box>
    </Box>
  );
};

export default ProgressIndicator;